import React from "react";
import { Button, Dialog, DialogContent, DialogTitle, Grid } from "@mui/material";
import moment from "moment";
import { FileDownloadOutlined } from "@mui/icons-material";
import { InfoOutlined } from "@mui/icons-material";

import LoadingSpinner from "modules/common/components/loadingSpinner";
import ChipList from "modules/common/components/chipList";
import PreviewPhone from "./previewPhone";
import { useSetSnackbarMessages } from "modules/common/hooks/useSetSnackbarMessages";
import { getAndDownloadAudienceUsers } from "./helpers/commonSMSFunctions";
import Disclaimer from "modules/common/components/disclaimer";
import { ISMSPreviewDetails } from "./models";

import "./styles/smsOneLastCheck.sass";

interface ISMSOneLastCheckProps {
    show: boolean;
    onClose: () => void;
    sending: boolean;
    sendSMS: () => void;
    totalAudienceUsers: number;
    audienceUsers: number;
    showDisclaimer?: boolean;

    downloadOptedInUsers: boolean;
    hideSentTime: boolean;
    smsDetails: ISMSPreviewDetails;
}

const SMSOneLastCheck: React.FC<ISMSOneLastCheckProps> = ({
    show,
    onClose,
    sending,
    sendSMS,
    totalAudienceUsers,
    audienceUsers,
    downloadOptedInUsers,
    hideSentTime,
    smsDetails,
    showDisclaimer = false
}) => {
    const [fetchingAudienceCSV, setFetchingAudienceCSV] = React.useState(false);
    const { setSuccessMessage, setErrorMessage } = useSetSnackbarMessages();
    const labelGridSize = 2;
    const dataGridSize = 10;

    const audiencesToShow = smsDetails.sendToAllUsers ? [] : (smsDetails.audiences ?? []);

    const downloadAudienceUsers = async () => {
        const audienceIds = smsDetails.audiences.map(a => a.id);
        const fileName = downloadOptedInUsers ? "send-sms-to" : "send-invite-to";

        try {
            setFetchingAudienceCSV(true);
            return await getAndDownloadAudienceUsers(audienceIds, smsDetails.sendToAllUsers, downloadOptedInUsers, fileName, setSuccessMessage, setErrorMessage);
        }
        finally {
            setFetchingAudienceCSV(false);
        }
    }

    return <Dialog
        open={show}
        className="sms-one-last-check-dialog"
        fullWidth
        maxWidth={"md"}
        PaperProps={{
            style: {
                maxWidth: 897
            }
        }}
        scroll="body"
        onClose={() => !sending && onClose()}
    >
        <DialogTitle className="sms-check-dialog-title">
            <span>One last check!</span>
            <div>
                <Button variant="text" color="primary" onClick={onClose} disabled={sending}>
                    KEEP EDITING
                </Button>
                {
                    sending 
                        ? <div className="loading-spinner-container"><LoadingSpinner size={"14px"} /></div>
                        : <Button variant="contained" color="primary" onClick={sendSMS} id="one-last-check-send-button">SEND</Button>
                }
            </div>
        </DialogTitle>
        <DialogContent className="sms-check-dialog-content">
            <Grid className="data-container" container spacing={2}>
                {showDisclaimer && <Grid item xs={12}>
                    <Disclaimer 
                        icon={<InfoOutlined className="data-disclaimer-icon"/>} 
                        text={"This invite will only be sent to employees who are not opted in to receive SMS alerts and notifications."} 
                    />
                </Grid>}

                {smsDetails.title &&
                    <>
                        <Grid item xs={labelGridSize} className="data-container-label">
                            Internal title
                        </Grid>
                        <Grid item xs={dataGridSize}>
                            {smsDetails.title}
                        </Grid>
                    </>
                }
                {!hideSentTime &&
                    <>
                        <Grid item xs={labelGridSize} className="data-container-label">
                            Send on
                        </Grid>
                        <Grid item xs={dataGridSize}>
                            {smsDetails.sentTime && new Date(smsDetails.sentTime) >= new Date() ? moment(smsDetails.sentTime).format("dddd, MMMM D, YYYY, h:mm A") : "Now"}
                        </Grid>
                    </>
                }
                <Grid item xs={labelGridSize} className="send-to-label data-container-label">
                    Send to
                </Grid>
                <Grid item xs={dataGridSize}>
                    <div>
                        <div className="x-of-y-employees-label">
                            <span>{audienceUsers} of {totalAudienceUsers} employees</span>
                            <Button 
                                className="download-button-sms-last-check"
                                onClick={downloadAudienceUsers} 
                                disabled={fetchingAudienceCSV}
                                startIcon={<FileDownloadOutlined/>}>
                                    Download CSV
                            </Button>
                        </div>
                        <ChipList 
                            items={audiencesToShow} 
                            emptyChipDetails={{show: true, contents: smsDetails.sendToAllUsers ? "All users" : "No recipients selected"}}
                            truncateAtIndex={4}
                        />
                    </div>
                </Grid>
            </Grid>
            <p className="preview-help">Actual SMS appearance varies per recipient and device.</p>
            <div>
                <PreviewPhone 
                    phoneNumber={smsDetails.fromNumber ?? process.env.REACT_APP_DEFAULT_SMS_NUMBER!} 
                    previewTextMessage={smsDetails.textBody}
                    drawerOpenPadding={false}
                    showDisclaimer={false}
                    overrideWidth="50%"
                />
            </div>
        </DialogContent>
    </Dialog>;
};

export default SMSOneLastCheck;
